// PairUpToday — IQ200 Dynamic Location Service
// Integrates Google Maps Places Autocomplete & Place Details with strict India restriction (IN)
// Persists structured location records to PostgreSQL and supports PostGIS radius searches.

import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { StructuredLocation, LocationAutocompleteSuggestion, SearchRadiusKm } from '../types/location';
import { apiFetch as fetch } from './apiConfig';
import {
  CuratedIndianLocation,
  findCuratedIndianLocationByPlaceId,
  searchCuratedIndianLocations,
} from '../data/indianLocationsData';

let dynamicMapsApiKey: string = (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY || '';
let configFetchPromise: Promise<string> | null = null;

export async function getClientMapsApiKey(): Promise<string> {
  if (dynamicMapsApiKey) return dynamicMapsApiKey;
  if (configFetchPromise) return configFetchPromise;

  configFetchPromise = (async () => {
    try {
      const res = await fetch('/api/v1/locations/client-config');
      if (res.ok) {
        const data = await res.json();
        if (data && data.apiKey) {
          dynamicMapsApiKey = String(data.apiKey).trim();
          return dynamicMapsApiKey;
        }
      }
    } catch {
      // Fallback: offline or non-blocking
    }
    return '';
  })();

  return configFetchPromise;
}

let googleMapsPromise: Promise<typeof google> | null = null;
let autocompleteService: google.maps.places.AutocompleteService | null = null;
let placesService: google.maps.places.PlacesService | null = null;
let dummyDiv: HTMLDivElement | null = null;

function curatedToSuggestion(loc: CuratedIndianLocation): LocationAutocompleteSuggestion {
  return {
    place_id: loc.google_place_id,
    description: loc.formatted_address,
    main_text: loc.main_text || loc.city,
    secondary_text: loc.secondary_text || `${loc.district}, ${loc.state} ${loc.postal_code}, India`,
    types: ['locality', 'political', 'geocode'],
  };
}

function curatedToStructuredLocation(loc: CuratedIndianLocation): StructuredLocation {
  return {
    place_id: loc.google_place_id,
    formatted_address: loc.formatted_address,
    latitude: loc.latitude,
    longitude: loc.longitude,
    city: loc.city,
    district: loc.district,
    state: loc.state,
    postal_code: loc.postal_code,
    country: loc.country || 'India',
    country_code: 'IN',
  };
}

/**
 * Initializes Google Maps Platform JavaScript SDK with Places library
 * Only attempts client-side initialization if VITE_GOOGLE_MAPS_API_KEY is explicitly configured
 * and not running inside a local Capacitor origin where HTTP referrer keys are rejected.
 */
export async function initGooglePlaces(): Promise<typeof google | null> {
  if (typeof window === 'undefined') return null;

  if ((window as any).google?.maps?.places) {
    if (!autocompleteService) {
      autocompleteService = new google.maps.places.AutocompleteService();
    }
    if (!placesService) {
      if (!dummyDiv) dummyDiv = document.createElement('div');
      placesService = new google.maps.places.PlacesService(dummyDiv);
    }
    return (window as any).google;
  }

  const isCapacitorLocal =
    window.location.protocol === 'capacitor:' ||
    window.location.origin === 'https://localhost' ||
    window.location.origin === 'http://localhost';

  if (isCapacitorLocal) {
    return null;
  }

  let activeApiKey = dynamicMapsApiKey;
  if (!activeApiKey) {
    activeApiKey = await getClientMapsApiKey();
  }

  if (!activeApiKey) {
    return null;
  }

  if (!googleMapsPromise) {
    setOptions({
      key: activeApiKey,
      v: 'weekly',
      region: 'IN',
    });

    googleMapsPromise = (async () => {
      try {
        await Promise.all([
          importLibrary('maps'),
          importLibrary('places'),
          importLibrary('geometry'),
          importLibrary('marker'),
        ]);
        const g = (window as any).google;
        if (g?.maps?.places) {
          autocompleteService = new g.maps.places.AutocompleteService();
          if (!dummyDiv) dummyDiv = document.createElement('div');
          placesService = new g.maps.places.PlacesService(dummyDiv);
        }
        return g;
      } catch (err) {
        googleMapsPromise = null;
        console.warn('Google Maps client loader notice, using backend/curated location search:', err);
        return null as any;
      }
    })();
  }

  return googleMapsPromise;
}

/**
 * Parses Google Place address components into structured database schema
 */
export function extractStructuredLocationFromPlace(place: google.maps.places.PlaceResult): StructuredLocation {
  const addressComponents = place.address_components || [];
  let city = '';
  let district = '';
  let state = '';
  let postalCode = '';
  let country = 'India';
  let countryCode = 'IN';

  for (const comp of addressComponents) {
    const types = comp.types || [];
    if (types.includes('locality')) {
      city = comp.long_name;
    } else if (!city && types.includes('sublocality_level_1')) {
      city = comp.long_name;
    } else if (!city && types.includes('administrative_area_level_3')) {
      city = comp.long_name;
    }

    if (types.includes('administrative_area_level_2')) {
      district = comp.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      state = comp.long_name;
    }
    if (types.includes('postal_code')) {
      postalCode = comp.long_name;
    }
    if (types.includes('country')) {
      country = comp.long_name || 'India';
      countryCode = comp.short_name || 'IN';
    }
  }

  if (!city) {
    city = place.name || district || state || 'India';
  }

  const lat = typeof place.geometry?.location?.lat === 'function'
    ? place.geometry.location.lat()
    : (place.geometry?.location as any)?.lat || 22.58;

  const lng = typeof place.geometry?.location?.lng === 'function'
    ? place.geometry.location.lng()
    : (place.geometry?.location as any)?.lng || 88.46;

  return {
    place_id: place.place_id || `place_${Date.now()}`,
    formatted_address: place.formatted_address || `${city}, ${state}, India`,
    latitude: Number(lat.toFixed(7)),
    longitude: Number(lng.toFixed(7)),
    city,
    district: district || undefined,
    state: state || 'West Bengal',
    postal_code: postalCode || undefined,
    country,
    country_code: countryCode.toUpperCase(),
  };
}

/**
 * Searches location suggestions with India (IN) restriction.
 * Combines backend /api/v1/locations/autocomplete with curated Indian locations fallback
 * and supports AbortSignal cancellation to prevent out-of-order stale responses.
 */
export async function searchPlaceAutocomplete(
  input: string,
  signal?: AbortSignal
): Promise<LocationAutocompleteSuggestion[]> {
  const query = (input || '').trim();
  if (!query || query.length < 2) return [];
  if (signal?.aborted) return [];

  const localMatches = searchCuratedIndianLocations(query, 8).map(curatedToSuggestion);

  // 1. Query backend /api/v1/locations/autocomplete with timeout & abort support
  try {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), 3000);
    const onExternalAbort = () => timeoutController.abort();
    signal?.addEventListener('abort', onExternalAbort, { once: true });

    try {
      const res = await fetch(`/api/v1/locations/autocomplete?q=${encodeURIComponent(query)}`, {
        signal: timeoutController.signal,
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.success && Array.isArray(data.suggestions)) {
          const seen = new Set<string>();
          const merged: LocationAutocompleteSuggestion[] = [];
          for (const item of [...data.suggestions, ...localMatches]) {
            if (!item || !item.place_id || seen.has(item.place_id)) continue;
            seen.add(item.place_id);
            merged.push(item);
          }
          return merged.slice(0, 8);
        }
      }
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onExternalAbort);
    }
  } catch (apiErr: any) {
    if (signal?.aborted || apiErr?.name === 'AbortError') {
      if (signal?.aborted) return [];
    }
  }

  // 2. Optional client-side Google Maps Places SDK if configured
  try {
    const g = await initGooglePlaces();
    if (autocompleteService && g && !signal?.aborted) {
      const response = await new Promise<google.maps.places.AutocompletePrediction[]>((resolve, reject) => {
        autocompleteService!.getPlacePredictions(
          {
            input: query,
            componentRestrictions: { country: 'in' },
          },
          (predictions, status) => {
            if (status === g.maps.places.PlacesServiceStatus.OK && predictions) {
              resolve(predictions);
            } else if (status === g.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              resolve([]);
            } else {
              reject(new Error(`PlacesServiceStatus: ${status}`));
            }
          }
        );
      });

      const sdkSuggestions = response.map((p) => ({
        place_id: p.place_id,
        description: p.description,
        main_text: p.structured_formatting?.main_text || p.description.split(',')[0],
        secondary_text: p.structured_formatting?.secondary_text || p.description.split(',').slice(1).join(',').trim(),
        types: p.types,
      }));
      if (sdkSuggestions.length > 0) {
        return sdkSuggestions;
      }
    }
  } catch {
    // Fall back to curated Indian locations
  }

  return localMatches;
}

/**
 * Fetches place details by Place ID and returns structured location data
 */
export async function fetchPlaceDetails(
  placeId: string,
  fallbackSuggestion?: LocationAutocompleteSuggestion
): Promise<StructuredLocation | null> {
  if (!placeId) return null;

  // 1. Check curated Indian locations first for instant resolution of curated place IDs
  const curated = findCuratedIndianLocationByPlaceId(placeId);
  if (curated) {
    const structured = curatedToStructuredLocation(curated);
    saveLocationToDatabase(structured).catch(() => {});
    return structured;
  }

  // 2. Query backend /api/v1/locations/details
  try {
    const res = await fetch(`/api/v1/locations/details?place_id=${encodeURIComponent(placeId)}`);
    if (res.ok) {
      const data = await res.json();
      if (data?.success && data.location) {
        return data.location;
      }
    }
  } catch (apiErr) {
    console.warn('Backend place details query notice:', apiErr);
  }

  // 3. Try client-side Google Places SDK if initialized
  try {
    const g = await initGooglePlaces();
    if (placesService && g) {
      const result = await new Promise<google.maps.places.PlaceResult>((resolve, reject) => {
        placesService!.getDetails(
          {
            placeId,
            fields: ['place_id', 'formatted_address', 'geometry', 'address_components', 'name'],
          },
          (place, status) => {
            if (status === g.maps.places.PlacesServiceStatus.OK && place) {
              resolve(place);
            } else {
              reject(new Error(`Place details status: ${status}`));
            }
          }
        );
      });

      const structured = extractStructuredLocationFromPlace(result);
      saveLocationToDatabase(structured).catch(() => {});
      return structured;
    }
  } catch {
    // Safe fallback below
  }

  if (fallbackSuggestion) {
    return {
      place_id: fallbackSuggestion.place_id,
      formatted_address: fallbackSuggestion.description,
      latitude: 22.5726,
      longitude: 88.3639,
      city: fallbackSuggestion.main_text || fallbackSuggestion.description.split(',')[0]?.trim() || 'Kolkata',
      state: 'India',
      country: 'India',
      country_code: 'IN',
    };
  }

  return null;
}

/**
 * Saves a validated structured location into the PostgreSQL locations table
 */
export async function saveLocationToDatabase(location: StructuredLocation): Promise<StructuredLocation> {
  try {
    const res = await fetch('/api/v1/locations/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(location),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.location) {
        return data.location;
      }
    }
  } catch (err) {
    console.warn('Error saving location to PostgreSQL database:', err);
  }
  return location;
}

/**
 * Geodesic distance calculation (Haversine formula, matching PostGIS ST_DWithin)
 * Calculates distance in kilometers between two lat/lon coordinates.
 */
export function calculateDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Number((R * c).toFixed(2));
}

/**
 * Checks if a target location is within the given radius (in km) from a center point
 * PostGIS query equivalent: ST_DWithin(geography(p1), geography(p2), radius_meters)
 */
export function isWithinRadius(
  centerLat: number,
  centerLon: number,
  targetLat: number,
  targetLon: number,
  radiusKm: SearchRadiusKm
): boolean {
  const dist = calculateDistanceKm(centerLat, centerLon, targetLat, targetLon);
  return dist <= radiusKm;
}

/**
 * Builds direct Google Maps navigation URL with place_id and coordinates
 */
export function getGoogleMapsNavigationUrl(location: StructuredLocation | string): string {
  if (typeof location === 'string') {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
  }
  const dest = `${location.latitude},${location.longitude}`;
  const placeParam = location.place_id && !location.place_id.startsWith('place_')
    ? `&destination_place_id=${encodeURIComponent(location.place_id)}`
    : '';
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}${placeParam}`;
}
